import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useSocket } from "@data-phone/react-resource-server-auth";

import { useAppDispatch } from "../redux/hooks";
import { RootState, store } from "../redux/store";
import {
  addOrUpdateLiveCall,
  LiveCall,
  removeLiveCall,
  setLiveCalls,
} from "../redux/slices/liveCalls.slice";
import { updateExtensionsObj, updateMultipleExtensionsObj, addExtensionDevice, removeExtensionDevice, updateExtensionsPhoneFeatures } from "../redux/slices/apps.slice";

import { useIntegrationLookups } from "./integrations";
import { getPhoneFeatures as getPhoneFeaturesService, handlePhoneFeature as handlePhoneFeatureService, PhoneFeature } from "../services/phone-features.service";

import { Utils } from "utils";
import { message } from "antd";
interface Callee {
  uuid: string;
  number: string;
  answerState: string;
}

interface CallFlowEntry {
  time: string;
  number: string;
}

interface WebSocketCallData {
  callUUID: string;
  callerIDName?: string;
  callerIDNumber?: string;
  startTime?: string;
  answerState?: string;
  answerTime?: string;
  dialedNumber?: string;
  destination?: string;
  callDirection?: "inbound" | "outbound";
  callFlow?: CallFlowEntry[];
  isHolding?: boolean;
  isParked?: boolean;
  isRecording?: boolean;
  callees?: Callee[];
}

interface CallbackResponse<T = any> {
  code: number;
  message: string;
  data?: T;
  error?: string;
}

export const useCallSocket = () => {
  const dispatch = useAppDispatch();
  const { fetchIntegrationLookups } = useIntegrationLookups();

  const { socket, changeAccount } = useSocket({
    socketUrl: `${process.env.REACT_APP_LIVE_CALL_WSS}/activeCalls`,
    path: "",
  });

  const liveCalls = useSelector((state: RootState) => state.liveCalls.calls);
  const { account_id } = useSelector((state: RootState) => state.appConfig);
  const integrations = useSelector((state: RootState) => state.integrations.items);
  const extensions = useSelector((state: RootState) => state.apps.data.extension);

  const [initialCallsToLookup, setInitialCallsToLookup] = useState<string[]>([]);
  const liveCallsRef = useRef<LiveCall[]>([]);

  const previousAccountIdRef = useRef<string | null>(null);

  liveCallsRef.current = liveCalls;

  const normalizeCall = (call: WebSocketCallData): LiveCall => ({
    callUUID: call.callUUID,
    callerIDName: call.callerIDName ?? "",
    callerIDNumber: call.callerIDNumber?.trim() ?? "",
    startTime: call.startTime ?? new Date().toISOString(),
    duration: "",
    answerState: call.answerState ?? "",
    callees: call.callees ?? [],
    callDirection: call.callDirection ?? "inbound",
    callFlow: call.callFlow ?? [],
    destination: call.destination?.trim() ?? "",
    dialedNumber: call.dialedNumber ?? "",
    isHolding: !!call.isHolding,
    isParked: !!call.isParked,
    isRecording: !!call.isRecording,
    answerTime: call.answerTime ?? "",
  });

  const updateExtensionStatuses = (callees?: Callee[]) => {
    try {
      if (!callees?.length) return;

      callees.forEach((callee) => {
        const extensionNumber = callee.number?.trim();
        if (!extensionNumber) return;
        const extensionStatus = Utils.getExtensionStatus(callee.answerState);

        dispatch(
          updateExtensionsObj({
            extension: extensionNumber,
            status: extensionStatus,
            state: callee.answerState,
          })
        );
      });
    } catch (error) {
      console.error("Error updating extension statuses:", error);
    }
  };

  const extractPhoneNumbersForLookup = (calls: WebSocketCallData[]): string[] => {
    return calls
      .map((call) =>
        call.callDirection === 'inbound' ? call.callerIDNumber : call.destination
      )
      .filter((num): num is string =>
        typeof num === 'string' && Utils.isValidPhoneNumberForCountry(num)
      )
      .map((num) => Utils.phoneNumberFormatE164(num))
      .filter((num): num is string => typeof num === 'string');
  };

  const performIntegrationLookup = (callerIDNumber?: string) => {
    if (!callerIDNumber || !Utils.isValidPhoneNumberForCountry(callerIDNumber)) return;

    const formatted = Utils.phoneNumberFormatE164(callerIDNumber);
    if (formatted) {
      fetchIntegrationLookups([formatted]);
    }
  };

  const getCalls = () => {
    if (!socket) return;

    socket.emit("get-calls", (res: CallbackResponse<LiveCall[]>) => {
      try {
        if (!res.data) {
          console.error("Error fetching calls:", res.error);
          return;
        }

        if (!Array.isArray(res.data)) {
          throw new Error("get-calls payload is not an array");
        }

        const liveCalls: LiveCall[] = res.data.map(normalizeCall);
        dispatch(setLiveCalls(liveCalls));

        res.data.forEach((call) => updateExtensionStatuses(call.callees));

        const numbersToLookup = extractPhoneNumbersForLookup(res.data);
        setInitialCallsToLookup(numbersToLookup);
      } catch (error) {
        console.error("Error handling get-calls:", error);
      }
    });
  };

  const handleHangUp = (callUUID: string) => {
    try {
      if (!socket) return;
      socket.emit("hangup", callUUID, (err?: Error) => {
        if (err) {
          console.error("Error during hangup emit:", err);
        }
      });
    } catch (error) {
      console.error("Error in handleHangUp:", error);
    }
  };

  const handleRecord = (callUUID: string, action: "start" | "stop"): void => {
    try {
      if (!socket) return;

      socket.emit("record", callUUID, action, (err?: Error) => {
        if (err) {
          console.error("Error during record emit:", err);
        }
      });
    } catch (error) {
      console.error("Error in handleRecord:", error);
    }
  };

  const handleTransfer = (callUUID: string, target: string) => {
    try {
      if (!socket) return;

      socket.emit("transfer", callUUID, target, (err?: any) => {
        if (err) {
          message.error({ content: err?.message || "There was an error while transfering call." })
        }
      });
    } catch (error) {
      console.error("Error in handleTransfer:", error);
    }
  };

  const lookedUpNumbers = new Set<string>();

  const handleCallUpdate = (updatedCall: WebSocketCallData) => {
    try {
      if (!updatedCall || typeof updatedCall !== "object") {
        throw new Error("Invalid call-update payload");
      }

      const normalized = normalizeCall(updatedCall);

      updateExtensionStatuses(updatedCall.callees);

      if (normalized.answerState?.toLowerCase() === "hangup") {
        dispatch(removeLiveCall(normalized.callUUID));
        return;
      }

      dispatch(addOrUpdateLiveCall(normalized));
      const callerIDNumber = updatedCall.callDirection === 'inbound' ? normalized.callerIDNumber : normalized.destination

      if (!lookedUpNumbers.has(callerIDNumber)) {
        lookedUpNumbers.add(callerIDNumber);
        performIntegrationLookup(callerIDNumber);
      }
    } catch (error) {
      console.error("Error handling call-update:", error);
    }
  };

  const handleCallDestroy = (payload: any) => {
    try {
      const callUUID = typeof payload === "string" ? payload : payload?.callUUID;
      if (!callUUID) {
        throw new Error("Invalid call-destroy payload");
      }

      const removedCall = liveCallsRef.current.find((call) => call.callUUID === callUUID);
      const callees = removedCall?.callees ?? [];

      callees.forEach((callee) => {

        dispatch(
          updateExtensionsObj({
            extension: callee.number.trim(),
            status: "Available",
            state: "idle",
          })
        );
      });

      dispatch(removeLiveCall(callUUID));
    } catch (error) {
      console.error("Error handling call-destroy:", error);
    }
  };

  const handleUserOnline = (online: boolean, payload: any) => {
    try {
      if (!payload || typeof payload !== "object") {
        throw new Error("Invalid user-online payload");
      }

      const { user, networkIp, networkPort, userAgent } = payload;

      if (!user) {
        throw new Error("Missing required field 'user' in user-online/offline payload");
      }

      const extensionNumber = user.split("@")[0];

      if (online) {
        dispatch(
          addExtensionDevice({
            extension: extensionNumber,
            deviceInfo: {
              networkIp: networkIp,
              networkPort: networkPort,
              userAgent: userAgent
            }
          })
        );
      } else {
        dispatch(
          removeExtensionDevice({
            extension: extensionNumber,
            deviceInfo: {
              networkIp: networkIp,
              networkPort: networkPort,
              userAgent: userAgent
            }
          })
        );
      }
    } catch (error) {
      console.error("Error handling user-online:", error);
    }
  };

  const getUserRegistrations = () => {
    try {

      socket.emit("get-user-registrations", (res: any) => {
        if (!res.data || !Array.isArray(res.data)) return;
        
        const extensionsMap = new Map<string, any[]>();
        
        res.data.forEach((item: any) => {
          const extensionNumber = item.user.split("@")[0];
          const deviceData = {
            networkIp: item.networkIp,
            networkPort: item.networkPort,
            userAgent: item.user_agent,
          };
          
          if (extensionsMap.has(extensionNumber)) {
            extensionsMap.get(extensionNumber)!.push(deviceData);
          } else {
            extensionsMap.set(extensionNumber, [deviceData]);
          }
        });
        
        const extensions = Array.from(extensionsMap.entries()).map(([extension, devices]) => ({
          extension,
          extensionDevices: devices,
        }));
        
        dispatch(updateMultipleExtensionsObj({ extensions }));
      });
    } catch (error) {
      console.error("Error getting user registrations:", error);
    }
  };

  const getPhoneFeatures = () => {
    getPhoneFeaturesService(socket, (phoneFeatures) => {
      dispatch(updateExtensionsPhoneFeatures(phoneFeatures));
    });
  };

  const handlePhoneFeature = (payload: PhoneFeature) => {
    handlePhoneFeatureService(payload, (phoneFeature) => {
      dispatch(updateExtensionsPhoneFeatures([phoneFeature]));
    });
  };

  useEffect(() => {
    const shouldFetchLookups =
      integrations.length > 0 &&
      integrations.every((item) => item.config && Object.keys(item.config).length > 0) &&
      initialCallsToLookup.length > 0;

    if (shouldFetchLookups) {
      fetchIntegrationLookups(initialCallsToLookup);
      setInitialCallsToLookup([]);
    }
  }, [integrations, initialCallsToLookup]);

  useEffect(() => {
    if (!account_id) return;
    if (!socket) return;

    changeAccount(account_id);
  }, [account_id, socket]);

  useEffect(() => {
    if (!extensions.length || !account_id) return;

    if (previousAccountIdRef.current !== account_id) {
      getUserRegistrations();
      getPhoneFeatures();
      getCalls();
      previousAccountIdRef.current = account_id;
    }
  }, [extensions, account_id])

  useEffect(() => {
    if (!socket) return;

    const handleConnect = () => { };
    const handleDisconnect = () => { };
    const handleConnectError = (err: Error) => console.error("Connect error:", err);

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("connect_error", handleConnectError);
    socket.on("call-update", handleCallUpdate);
    socket.on("call-destroy", handleCallDestroy);
    socket.on("user-online", (data: any) => {
      handleUserOnline(true, data);
    });
    socket.on("user-offline", (data: any) => {
      handleUserOnline(false, data);
    });
    socket.on("phone-feature", handlePhoneFeature);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleConnectError);
      socket.off("call-update", handleCallUpdate);
      socket.off("call-destroy", handleCallDestroy);
      socket.off("phone-feature", handlePhoneFeature);
      socket.disconnect();
    };
  }, [socket, dispatch]);

  return {
    handleHangUp,
    handleRecord,
    handleTransfer,
    getCalls,
  };
};
